import React, { ChangeEvent, useEffect, useState } from "react";
import { getCountries } from "../../api/countries/countriesApi";
import { getMapMarkers } from "../../api/mapMarkers/mapMarkersApi";
import { getTags } from "../../api/tags/tagsApi";
import AddEditVideo from "./AddEditVideo";
import { useNavigate, useParams } from "react-router-dom";
import {
  Country,
  MapMarker,
  Species,
  Video,
  Tag,
  TimeSpan,
  VideoFormatDto,
  VideoStatusDto,
  VideoResolutionDto,
  UploadStatus,
  Organization,
} from "../common/ObjectTypes";
import { getSpecies } from "../../api/species/speciesApi";
import {
  createVideo,
  getVideoDetails,
  updateVideo,
  updateVideoSpecies,
  updateVideoMapMarkers,
  updateVideoTags,
  uploadThumbnail,
  generatSasUrlForVideo,
  startEncoding,
  deleteVideo,
} from "../../api/videos/videosApi";
import { toast } from "react-toastify";
import { MoonLoader } from "react-spinners";
import { getVideoFormats } from "../../api/videoFormats/videoFormatsApi";
import { getVideoStatuses } from "../../api/videoStatuses/videoStatusesApi";
import { PHOTO_URL } from "../../base";
import { getVideoResolutions } from "../../api/videoResolutions/videoResolutionsApi";
import { BlobServiceClient } from "@azure/storage-blob";
import UploadVideoForm from "./UploadVideoForm";
import { getOrganizations } from "../../api/organizations/organizationsApi";
import { getCurrentUser } from "../../helpers/authHeader";
import { getWildXROrgId } from "../../helpers/wildXROrgIdHelper";

type ManageAddEditVideoProps = {
  existingVideo: boolean;
  uploadVideoPage?: boolean;
};

const ManageAddEditVideo = ({
  existingVideo,
  uploadVideoPage,
}: ManageAddEditVideoProps) => {
  const navigate = useNavigate();
  const videoId = useParams<{ id: string }>().id;
  const [pageLoaded, setPageLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [video, setVideo] = useState<Video>({
    videoId: "",
    thumbnailUrl: "",
    name: "",
    dateCreated: "",
    lastEditedBy: "",
    lastEditedDate: "",
    overview: "",
    countryObtainedId: "", // id of country
    species: [],
    tags: [],
    mapMarkers: [],
    rowVersion: "",
    streamingUrl: "",
    downloadUrl: "",
    totalViews: 0,
    totalLikes: 0,
    totalDislikes: 0,
    rating: "",
    youTubeUrl: "",
    startTime: "00:00:00", // initialize as string
    endTime: "00:00:00", // initialize as string
    showInAllVideos: false,
    compilation: false,
    gameDrive: false,
    graphicSex: false,
    graphicDeath: false,
    videoFormat: "",
    videoStatusId: 0,
    videoResolutionId: "",
    organizationId: "",
    videoFileName: "",
  });
  const { v4: uuidv4 } = require("uuid");
  const currentUser = getCurrentUser();
  const [countries, setCountries] = useState<Country[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>([]);
  const [areAllMapMarkersSelected, setAreAllMapMarkersSelected] =
    useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [videoFormats, setVideoFormats] = useState<VideoFormatDto[]>([]);
  const [videoStatuses, setVideoStatuses] = useState<VideoStatusDto[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [changesMade, setChangesMade] = useState(false);
  const [thumbnailDataUrl, setThumbnailDataUrl] = useState<string | null>(null);
  const [thumbnailName, setThumbnailName] = useState<string | null>(null);
  const [videoResolutions, setVideoResolutions] = useState<
    VideoResolutionDto[]
  >([]);
  const wildXRId = getWildXROrgId();

  const photoUrl = PHOTO_URL || "placeholder.jpg";
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    message: "",
    uploadStatusId: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  const isSystemAdmin = currentUser?.isSystemAdmin;
  const hasOrganizationId = video?.organizationId;
  const isInUserOrganizations = currentUser?.organizations
    ?.map((org: any) => org.organizationId)
    ?.includes(video.organizationId);
  const isDisabled =
    existingVideo &&
    !isSystemAdmin &&
    (!hasOrganizationId || !isInUserOrganizations);

  useEffect(() => {
    const loadVideo = async () => {
      // Load from API or initialize as needed
      if (!videoId) {
        await loadSpecies();
        await loadTags();
        await loadCountries();
        await loadVideoFormats();
        await loadVideoStatuses();
        await loadVideoResolutions();
        await loadOrganizations();
        setPageLoaded(true);
        return;
      }
      try {
        const temp = await getVideoDetails(videoId ?? "");
        await loadSpecies();
        await loadTags();
        await loadCountries();
        await loadVideoFormats();
        await loadVideoStatuses();
        await loadVideoResolutions();
        await loadOrganizations();
        setVideo(temp);
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };
    const loadCountries = async () => {
      try {
        const temp = await getCountries(1000);
        setCountries(temp);
      } catch (err) {
        console.log(err);
      }
    };

    const loadSpecies = async () => {
      try {
        const temp = await getSpecies(1000);
        setSpecies(temp);
      } catch (err) {
        console.log(err);
      }
    };

    const loadTags = async () => {
      try {
        const temp = await getTags(1000);
        setTags(temp);
      } catch (err) {
        console.log(err);
      }
    };

    const loadVideoFormats = async () => {
      try {
        const temp = await getVideoFormats();
        setVideoFormats(temp);
      } catch (err) {
        console.log(err);
      }
    };
    const loadVideoStatuses = async () => {
      try {
        const temp = await getVideoStatuses();
        setVideoStatuses(temp);
      } catch (err) {
        console.log(err);
      }
    };
    const loadVideoResolutions = async () => {
      try {
        const temp = await getVideoResolutions();
        setVideoResolutions(temp);
      } catch (err) {
        console.log(err);
      }
    };

    const loadOrganizations = async () => {
      try {
        // let temp;
        // if (!currentUser.isSystemAdmin && !currentUser.isHeadsetAdmin) {
        //   temp = currentUser.organizations;
        // } else {
        let temp = await getOrganizations();
        // }
        setOrganizations(temp);
      } catch (err) {
        console.log(err);
      }
    };

    loadVideo();
  }, [videoId, uploadVideoPage]);

  // Handle file input change
  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleUpload: React.MouseEventHandler<HTMLButtonElement> = async () => {
    if (!selectedFile) {
      alert("Please select a file to upload.");
      return;
    }

    if (!selectedFile.type.startsWith("video/")) {
      alert("Please upload a valid video file.");
      return;
    }

    try {
      // Retrieve the SAS URL
      const sasUrl = await generatSasUrlForVideo();
      // Check if the SAS URL is valid
      if (!isValidUrl(sasUrl)) {
        alert("Invalid SAS URL!");
        return;
      }

      const blobServiceClient = new BlobServiceClient(sasUrl);
      const containerClient = blobServiceClient.getContainerClient("");

      // Get the file extension from the original file name
      const fileExtension = selectedFile.name.split(".").pop();

      // Create the new file name using the videoId and original file extension
      const newFileName = `${videoId}.${fileExtension}`;

      // Create a block blob client using the new file name
      const blockBlobClient = containerClient.getBlockBlobClient(newFileName);

      setUploadStatus({ message: "Uploading...", uploadStatusId: 1 });
      // Upload the file with the new name
      await blockBlobClient.uploadData(selectedFile);
      const updatedVideo = {
        name: video.name,
        videoId: video.videoId,
        overview: video.overview,
        thumbnailUrl: video.thumbnailUrl,
        youTubeUrl: video.youTubeUrl,
        mapMarkers: [],
        startTime: convertToTimeSpan(video.startTime),
        endTime: convertToTimeSpan(video.endTime),
        countryObtainedId: video.countryObtainedId,
        videoResolutionId: video.videoResolutionId,
        tags: [],
        lastEditedDate: video.lastEditedDate
          ? video.lastEditedDate
          : new Date(),
        species: [],
        videoFormat: video.videoFormat,
        videoStatusId: 2,
        organizationId: video.organizationId,
        videoFileName: selectedFile.name,
      };

      // Send the update to the backend
      await updateVideo(updatedVideo);
      setUploadStatus({
        message: "File Uploaded to Azure Successfully!",
        uploadStatusId: 2,
      });
    } catch (error: any) {
      console.error("Upload failed with error:", error.message);
      setUploadStatus({
        message: "Upload failed. Please try again.",
        uploadStatusId: 3,
      });
    }
  };

  const handleActivateVideo = async (e: any) => {
    // Prevent page reload
    e.preventDefault();

    try {
      const updatedVideo = {
        name: video.name,
        videoId: video.videoId,
        overview: video.overview,
        thumbnailUrl: video.thumbnailUrl,
        youTubeUrl: video.youTubeUrl,
        mapMarkers: [],
        startTime: convertToTimeSpan(video.startTime),
        endTime: convertToTimeSpan(video.endTime),
        countryObtainedId: video.countryObtainedId,
        videoResolutionId: video.videoResolutionId,
        tags: [],
        lastEditedDate: video.lastEditedDate
          ? video.lastEditedDate
          : new Date(),

        species: [],
        videoFormat: video.videoFormat,
        videoStatusId: 1,
        organizationId: video.organizationId,
        videoFileName: video.videoFileName,
      };

      await updateVideo(updatedVideo);
      navigate("/");
      toast.success("Video activated!", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
    } catch (error) {
      console.error("Failed to activate video:", error);
    }
  };

  const isValidUrl = (urlString: any) => {
    // Ensure urlString is actually a string before calling trim()
    if (typeof urlString !== "string") {
      console.error("urlString is not a string!");
      return false;
    }

    const trimmedUrl = urlString.trim(); // Remove any extra whitespace

    try {
      new URL(trimmedUrl); // Validate the trimmed URL
      return true;
    } catch (error) {
      console.error("Invalid URL:", error);
      return false;
    }
  };

  const handleStartEncoding = async (videoId: string) => {
    try {
      startEncoding(videoId);
      toast.success("Encoding started successfully!", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      navigate("/");
      // if (response?.status === 200) {
      //   toast.success("Video encoded successfully!", {
      //     position: toast.POSITION.TOP_RIGHT,
      //     hideProgressBar: true,
      //     autoClose: 2500,
      //   });
      // }
    } catch (error) {
      console.error("Failed to start encoding:", error);
    }
  };

  const handleChangeThumbnail = (event: any) => {
    var input = event.target;
    if (input.files.length <= 0) {
      var thumbnailElement = document.getElementById(
        "thumbnail"
      ) as HTMLImageElement;
      thumbnailElement.src = photoUrl + video.thumbnailUrl;
    } else {
      // Validate that the file is a PNG
      const file = input.files[0];
      if (file.type !== "image/png") {
        alert("Please upload a PNG file.");
        input.value = ""; // Reset the input
        return;
      }

      var reader = new FileReader();
      reader.onload = function () {
        var thumbnailDataUrl = reader.result;
        var thumbnailElement = document.getElementById(
          "thumbnail"
        ) as HTMLImageElement;
        if (thumbnailElement != null) {
          thumbnailElement.src = thumbnailDataUrl as string;
          setThumbnailDataUrl(thumbnailDataUrl as string);
        }
      };
      reader.readAsDataURL(file);
      setVideo((prevVideo) => ({
        ...prevVideo,
        thumbnailUrl: file ? file.name : prevVideo.thumbnailUrl,
      }));
      setChangesMade(true);
      // localStorage.setItem("thumbnailName", file.name);
      setThumbnailName(file.name);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    // Update the video state
    setVideo((prevVideo) => ({
      ...prevVideo,
      [name]: newValue,
    }));
    setChangesMade(true);
  };

  const handleChangeSingleSelect = (name: string, e: any) => {
    setVideo((prevVideo: any) => {
      const newValue = e === null ? null : e.value;
      const updatedVideo = {
        ...prevVideo,
        [name]: newValue,
      };
      return updatedVideo;
    });
    setChangesMade(true);
  };

  const handleSelectMapMarkerForVideo = (
    selectedMapMarker: MapMarker,
    isChecked: boolean
  ) => {
    setVideo((prevVideo: any) => {
      const currentMapMarkers = prevVideo.mapMarkers || []; // Ensure mapMarkers is always an array

      // Create the hard-coded mapMarker object
      const mapMarkerObject = {
        mapMarkerId: selectedMapMarker.mapMarkerId,
        name: selectedMapMarker.name, // Assuming 'name' is correct
        rowVersion: selectedMapMarker.rowVersion,
        videos: selectedMapMarker.videos || [],
        latitude: selectedMapMarker.latitude,
        longitude: selectedMapMarker.longitude,
        iconID: selectedMapMarker.iconID,
        description: selectedMapMarker.description || "",
        organization: selectedMapMarker.organizationId || "",
        organizationId: selectedMapMarker.organizationId || "", // Adding this for consistency
      };

      // Update the mapMarkers array based on isChecked
      const newMapMarkers = isChecked
        ? currentMapMarkers.some(
            (p: any) => p.mapMarkerId === selectedMapMarker.mapMarkerId
          )
          ? currentMapMarkers
          : [...currentMapMarkers, mapMarkerObject]
        : currentMapMarkers.filter(
            (p: any) => p.mapMarkerId !== selectedMapMarker.mapMarkerId
          );

      // Create the updated video object
      const updatedVideo = {
        ...prevVideo,
        mapMarkers: newMapMarkers,
      };

      return updatedVideo;
    });
    setChangesMade(true);
  };

  const handleSelectAllMapMarkers = async () => {
    setIsLoading(true); // Start loading
    try {
      // Get current custom map markers (those with organizationId !== wildXRId)
      const customMapMarkers = (video.mapMarkers || []).filter(
        (mapMarker: any) => {
          return mapMarker.organizationId !== wildXRId;
        }
      );

      if (areAllMapMarkersSelected) {
        // Revert to original mapMarkers associated with the video
        const updatedMapMarkers = [...customMapMarkers];
        setVideo((prevVideo: any) => ({
          ...prevVideo,
          mapMarkers: updatedMapMarkers,
        }));
        // Force update on areAllMapMarkersSelected state to ensure UI re-renders correctly
        setAreAllMapMarkersSelected(false);

        // Force a state change to trigger re-render in child components
        setMapMarkers([...mapMarkers]);
      } else {
        // Fetch all mapMarkers and their associated videos
        const allMapMarkers = await getMapMarkers();
        // Filter to include only wildXR map markers
        const wildXRMapMarkers = allMapMarkers.filter((mapMarker: any) => {
          return mapMarker.organizationId === wildXRId;
        });

        // Combine wildXR map markers with existing custom map markers
        const combinedMapMarkers = [...wildXRMapMarkers, ...customMapMarkers];

        setVideo((prevVideo: any) => ({
          ...prevVideo,
          mapMarkers: combinedMapMarkers,
        }));

        // Set the flag to indicate all wildXR markers are selected
        setAreAllMapMarkersSelected(true);

        // Force a state change to trigger re-render in child components
        setMapMarkers([...mapMarkers]);
      }
    } catch (error) {
      console.error("Error selecting/deselecting all mapMarkers:", error);
    }
    setChangesMade(true);
    setIsLoading(false); // End loading
  };

  // change species
  const handleChangeSpecies = (selectedOptions: any) => {
    let speciesArray: any = [];
    // Check if selectedOptions is an array or a single object
    speciesArray = selectedOptions.map((species: any) => ({
      speciesId: species.value, // Using 'value' instead of 'speciesId'
      name: species.label, // Using 'label' instead of 'name'
      speciesCategoryId: species.speciesCategoryId,
      colloquialName: species.colloquialName,
      scientificName: species.scientificName,
      description: species.description,
      iucnStatusId: species.iucnStatusId,
      populationTrendId: species.populationTrendId,
      rowVersion: species.rowVersion,
      videos: [],
    }));

    setVideo((prevVideo: any) => {
      const updatedVideo = {
        ...prevVideo,
        species: speciesArray,
      };
      return updatedVideo;
    });
    setChangesMade(true);
  };
  // change tags
  const handleChangeTags = (selectedOptions: any) => {
    if (selectedOptions) {
      setVideo((prevVideo: any) => {
        let tagsArray = [];
        // Check if selectedOptions is an array or a single object
        if (Array.isArray(selectedOptions)) {
          tagsArray = selectedOptions.map((tag: any) => ({
            tagId: tag.value, // Using 'value' instead of 'tagId'
            name: tag.label, // Using 'label' instead of 'name'
          }));
        } else {
          tagsArray = [
            {
              tagId: selectedOptions.value, // Using 'value' instead of 'tagId'
              name: selectedOptions.label, // Using 'label' instead of 'name'
            },
          ];
        }
        const updatedVideo = {
          ...prevVideo,
          tags: tagsArray,
        };
        return updatedVideo;
      });
    }
    setChangesMade(true);
  };

  const getSpeciesIdsFromVideo = () => {
    try {
      const speciesArray = video.species || [];
      const speciesIds = speciesArray.map((species: any) => species.speciesId);
      return speciesIds;
    } catch (error) {
      console.error("Failed to parse video from localStorage", error);
      return [];
    }
  };
  const getMapMarkerIdsFromVideo = () => {
    try {
      const mapMarkersArray = video.mapMarkers || [];
      const mapMarkerIds = mapMarkersArray.map(
        (mapMarker: any) => mapMarker.mapMarkerId
      );
      return mapMarkerIds;
    } catch (error) {
      console.error("Failed to parse video from localStorage", error);
      return [];
    }
  };
  const getTagIdsFromVideo = () => {
    try {
      const tagsArray = video.tags || [];
      const tagIds = tagsArray.map((tag: any) => tag.tagId);
      return tagIds;
    } catch (error) {
      console.error("Failed to parse video from localStorage", error);
      return [];
    }
  };

  const handleChangeTime = (fieldName: string, value: string) => {
    setVideo((prevVideo) => {
      const updatedVideo = {
        ...prevVideo,
        [fieldName]: value,
      };
      return updatedVideo;
    });
    setChangesMade(true);
  };

  const convertToTimeSpan = (timeString: string): string | null => {
    if (!timeString) return null;

    // Convert "HHMMSS" to "HH:MM:SS"
    const cleaned = timeString.replace(/\D/g, "");
    if (cleaned.length !== 6) return null; // Ensure it has exactly 6 digits

    const formattedTime = cleaned.replace(/(\d{2})(\d{2})(\d{2})/, "$1:$2:$3");

    // Ensure formattedTime is in HH:MM:SS format
    const timeRegex = /^(\d{2}):(\d{2}):(\d{2})$/;
    const match = formattedTime.match(timeRegex);
    if (!match) return null;

    const [hours, minutes, seconds] = match
      .slice(1)
      .map((part) => parseInt(part, 10));

    // Ensure parts are valid
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;

    // Convert to hh:mm:ss format
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };
  const formIsValid = (): boolean => {
    const newErrors: any = {};
    // const localStorageThumbnail = localStorage.getItem("thumbnailDataUrl");
    const thumbnailUrl = thumbnailDataUrl || video.thumbnailUrl;
    if (!video.name) {
      newErrors.name = "Video name is required";
    }
    if (!video.overview) {
      newErrors.overview = "Overview is required";
    }
    if (!thumbnailUrl) {
      newErrors.thumbnail = "Thumbnail is required";
    }
    if (!video.countryObtainedId) {
      newErrors.country = "Country is required";
    }
    if (!video.videoFormat) {
      newErrors.videoFormat = "Format is required";
    }
    if (!video.videoResolutionId) {
      newErrors.resolution = "Resolution is required";
    }

    if (!video.species || video.species.length === 0) {
      newErrors.species = "Species are required";
    }

    // if (!video.tags || video.tags.length === 0) {
    //   newErrors.tags = "Tags are required";
    // }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: any) => {
    e.preventDefault();
    const speciesIds = getSpeciesIdsFromVideo();
    const mapMarkerIds = getMapMarkerIdsFromVideo();
    const tagIds = getTagIdsFromVideo();

    // const localThumbnail = localStorage.getItem("thumbnailDataUrl");
    // let localThumbnailName = localStorage.getItem("thumbnailName");

    const updatedVideo = {
      name: video.name,
      videoId: video.videoId || uuidv4(),
      overview: video.overview,
      thumbnailUrl: video.thumbnailUrl,
      youTubeUrl: video.youTubeUrl,
      mapMarkers: [],
      startTime: convertToTimeSpan(video.startTime),
      endTime: convertToTimeSpan(video.endTime),
      countryObtainedId: video.countryObtainedId,
      videoResolutionId: video.videoResolutionId || undefined,
      tags: [],
      lastEditedDate: video.lastEditedDate || new Date(),
      species: [],
      videoFormat: video.videoFormat || undefined,
      videoStatusId: video.videoStatusId || undefined,
      organizationId: video.organizationId
        ? video.organizationId
        : currentUser.organizations.length === 1
        ? currentUser.organizations[0].organizationId
        : null,
      videoFileName: video.videoFileName || undefined,
    };

    try {
      setSaving(true);
      let canSave = formIsValid();
      if (!canSave) return;

      if (!existingVideo) {
        await createVideo(updatedVideo);
      } else {
        await updateVideo(updatedVideo);
      }

      // Rename the thumbnail file if videoId is newly created or updated
      const fileExtension = thumbnailName?.split(".").pop();
      const newThumbnailName = `${updatedVideo.videoId}.${fileExtension}`;
      // localStorage.setItem("thumbnailName", newThumbnailName);

      if (thumbnailDataUrl) {
        const thumbnailDto = {
          blobName: newThumbnailName,
          dataUrl: thumbnailDataUrl as string,
        };
        await uploadThumbnail(updatedVideo.videoId, thumbnailDto);
      }

      // Handle related data updates
      await updateVideoSpecies(updatedVideo.videoId, speciesIds);
      await updateVideoMapMarkers(updatedVideo.videoId, mapMarkerIds);
      await updateVideoTags(updatedVideo.videoId, tagIds);

      toast.success(video.name + (existingVideo ? " updated." : " added."), {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      if (!existingVideo) {
        navigate("/video/" + updatedVideo.videoId + "/uploadVideo");
      } else {
        navigate("/");
      }
      setPageLoaded(false);
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVideo = async (e: any, id: any) => {
    try {
      e.preventDefault();
      if (!window.confirm("Are you sure you want to delete this video?")) {
        return;
      }
      await deleteVideo(id);
      toast.success("Video deleted.", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      navigate("/");
    } catch (error) {
      console.error("Failed to delete video:", error);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  const handleUploadVideoPage = () => {
    navigate("/video/" + videoId + "/uploadVideo");
  };

  /**
   * Determines if the video should be disabled based on user permissions
   * Returns true if:
   * 1. The video exists AND
   * 2. The user is not a system admin AND
   * 3. Either the video has no organization ID OR the user is not in the video's organization
   */
  const determineIfVideoIsDisabled = () => {
    if (!currentUser || !video) return false;

    const isSystemAdmin = currentUser.isSystemAdmin;
    const hasOrganizationId = !!video.organizationId;

    // Check if user's organizations include the video's organization
    const isInUserOrganizations = currentUser.organizations?.some(
      (org: any) => org.organizationId === video.organizationId
    );

    const isDisabled =
      existingVideo &&
      !isSystemAdmin &&
      (!hasOrganizationId || !isInUserOrganizations);

    return isDisabled;
  };

  return !pageLoaded ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <div className="h-full">
      {!uploadVideoPage ? (
        <AddEditVideo
          video={video}
          existingVideo={existingVideo}
          countries={countries}
          errors={errors}
          species={species}
          mapMarkers={mapMarkers}
          tags={tags}
          videoFormats={videoFormats}
          videoStatuses={videoStatuses}
          organizations={organizations}
          videoResolutions={videoResolutions}
          onChange={handleChange}
          onChangeThumbnail={handleChangeThumbnail}
          onChangeSpecies={handleChangeSpecies}
          onSelectMapMarkerForVideo={handleSelectMapMarkerForVideo}
          onChangeTags={handleChangeTags}
          onChangeSingleSelect={handleChangeSingleSelect}
          onCancel={handleCancel}
          onSave={handleSave}
          onChangeTime={handleChangeTime}
          saving={saving}
          onFileInputChange={handleFileInputChange}
          onUpload={handleUpload}
          uploadStatus={uploadStatus}
          onUploadVideoPage={handleUploadVideoPage}
          onActivateVideo={handleActivateVideo}
          changesMade={changesMade}
          thumbnailDataUrl={thumbnailDataUrl}
          thumbnailName={thumbnailName}
          onDeleteVideo={handleDeleteVideo}
          onSelectAllMapMarkersForVideo={handleSelectAllMapMarkers}
          areAllMapMarkersSelectedForVideo={areAllMapMarkersSelected}
          isLoadingForVideo={isLoading}
        />
      ) : (
        <>
          <UploadVideoForm
            onFileInputChange={handleFileInputChange}
            onUpload={handleUpload}
            uploadStatus={uploadStatus}
            video={video}
            onBitmovinEncode={handleStartEncoding}
            isDisabled={isDisabled}
          />
        </>
      )}
    </div>
  );
};

export default ManageAddEditVideo;
